import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import s from './AnchoredPopover.module.scss';

type Coords = { top?: number; bottom?: number; left: number; maxHeight: number };

const MIN_HEIGHT = 180;

export interface AnchoredPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  children: ReactNode;
  width?: number;
  label?: string;
  align?: 'start' | 'end';
}

export const AnchoredPopover = ({
  open,
  onClose,
  anchorEl,
  children,
  width = 240,
  label,
  align = 'end',
}: AnchoredPopoverProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorEl) return;
    const place = () => {
      const rect = anchorEl.getBoundingClientRect();
      const estHeight = ref.current?.offsetHeight ?? 280;
      const openUpward = window.innerHeight - rect.bottom < estHeight && rect.top > estHeight;
      const preferred = align === 'start' ? rect.left : rect.right - width;
      const left = Math.max(8, Math.min(preferred, window.innerWidth - width - 8));
      const available = openUpward ? rect.top - 8 : window.innerHeight - rect.bottom - 8;
      const maxHeight = Math.max(MIN_HEIGHT, available - 8);
      setCoords(
        openUpward
          ? { bottom: window.innerHeight - rect.top + 8, left, maxHeight }
          : { top: rect.bottom + 8, left, maxHeight },
      );
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorEl, width, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorEl, onClose]);

  if (!open || !coords) return null;

  return createPortal(
    <div
      ref={ref}
      className={s.popover}
      role="dialog"
      aria-label={label}
      style={{
        position: 'fixed',
        top: coords.top !== undefined ? `${coords.top}px` : undefined,
        bottom: coords.bottom !== undefined ? `${coords.bottom}px` : undefined,
        left: `${coords.left}px`,
        width: `${width}px`,
        maxHeight: `${coords.maxHeight}px`,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default AnchoredPopover;
